/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Step 4: Unifying historical and new data", description = "Deduplicate and combine your historical data with new events collected by Hightouch so analytics stay continuous.", lastUpdatedDate = new Date(1782432000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Step 4: Unifying historical and new data",
        description: "Deduplicate and combine your historical data with new events collected by Hightouch so analytics stay continuous.",
        lastUpdatedDate: "Fri Jun 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Plan your unification strategy",
          "depth": 0,
          "id": "plan-your-unification-strategy"
        }, {
          "value": "Union historical and new data",
          "depth": 0,
          "id": "union-historical-and-new-data"
        }, {
          "value": "Validate the unified dataset",
          "depth": 0,
          "id": "validate-the-unified-dataset"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      ol: "ol",
      li: "li",
      strong: "strong",
      code: "code",
      pre: "pre",
      span: "span",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "After Hightouch Events is collecting data, decide how to combine new events with historical data from your previous provider. Most migrations need this step so dashboards, models, and destinations keep a continuous event history. (If you're rebuilding your data model or want a fresh start, you can skip it.)"
      }), "\n", _jsx(_components.h2, {
        id: "plan-your-unification-strategy",
        children: "Plan your unification strategy"
      }), "\n", _jsx(_components.p, {
        children: "Before you begin, consider the following:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Time frame"
          }), ": Based on your use cases, decide how far back you need to unify data (for example, 1 year, 2 years, or all historical data). Your decided time frame affects processing time and storage requirements and ensures you have enough historical context for your analyses."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data volume"
          }), ": Assess the volume of historical data you need to unify. This could impact processing time and storage costs and may influence your choice of unification method."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schema differences"
          }), ": Identify any differences between your old and new event schemas if you haven't in a prior step. If there are changes, this will inform any necessary data transformations during the unification process."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Storage considerations"
          }), ": Determine where the unified data will be stored (for example, an existing or new table in your data warehouse). Your decision can affect query performance, how you update downstream destinations, and may have cost implications."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You have a few options for where the unified data lives: the previous provider's tables, the Hightouch tables, or a separate unified table or view. ", _jsx(_components.strong, {
          children: "During validation, keep Hightouch data in its own schema"
        }), " so you can compare it against your historical data without modifying either set. For the long-term unified dataset, many teams use a separate unified table or view. In some warehouses, you may also choose to backfill historical rows into the Hightouch event tables after validation, but treat that as an implementation choice rather than the default recommendation."]
      }), "\n", _jsx(_components.h2, {
        id: "union-historical-and-new-data",
        children: "Union historical and new data"
      }), "\n", _jsx(_components.p, {
        children: "To keep your analytics continuous for data consumers, union the historical Segment data with the new Hightouch Events data."
      }), "\n", _jsx(_components.p, {
        children: "Your process will depend on where you've decided to store the unioned data and any needed data transformations. We've sketched in SQL here for inserting 2 years of historical data from a prior provider (in this case Segment) into the Hightouch tables with minimal transformation."
      }), "\n", _jsxs(_components.p, {
        children: ["In this particular case, we're only going to insert events from Segment that don't have a ", _jsx(_components.code, {
          children: "context_migration_id"
        }), ", since any dual-tracked event already exists in the Hightouch tables. The ", _jsx(_components.code, {
          children: "context_migration_id"
        }), " column only exists when your event storage destination writes context fields as columns; if it stores context as JSON, extract the value from the JSON column instead."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Insert historical data from Segment to Hightouch identifies table"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "INSERT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "INTO"
          }), " HIGHTOUCH.identifies (\n  id, anonymous_id, user_id, ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ", email, name, context_migration_id\n)\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n  s.id, s.anonymous_id, s.user_id, s.timestamp, s.email, s.name, s.context_migration_id\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " SEGMENT.identifies s\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " s.context_migration_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " s.timestamp ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " DATEADD(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "day"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "-730"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "());  ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Adjust time frame as needed"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Modify this for table names and to include any traits or other properties you need. Repeat similar INSERT statements for ", _jsx(_components.code, {
          children: "tracks"
        }), ", ", _jsx(_components.code, {
          children: "pages"
        }), ", and other relevant tables, mapping the columns appropriately."]
      }), "\n", _jsx(_components.p, {
        children: "If there are differences between Segment and Hightouch schemas, you may need to transform the data during insertion. If you are migrating from Segment, for example, the schemas are quite similar and should only require basic SQL transformations, if any."
      }), "\n", _jsx(_components.p, {
        children: "The example above uses Segment table and column names to keep it concrete. The approach is the same for any source—substitute your provider's names. See the RudderStack and mParticle guides for platform-specific notes."
      }), "\n", _jsx(_components.h2, {
        id: "validate-the-unified-dataset",
        children: "Validate the unified dataset"
      }), "\n", _jsx(_components.p, {
        children: "We've previously validated that Hightouch Events is collecting an equivalent volume of events with matching values. For peace of mind, after unifying the de-duped data, you can perform some basic validation steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check event counts"
          }), ": Ensure the total event count makes sense based on the historical data and new Hightouch events."]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n    DATE_TRUNC(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'day'"
          }), ", ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "date"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " event_count,\n    ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "DISTINCT"
          }), " user_id) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " user_count\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " HIGHTOUCH.tracks\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GROUP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ORDER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Verify data continuity"
          }), ": Look for any unexpected drops or spikes in event volume, especially around the migration period."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Compare with Segment data"
          }), ": Run a comparison query to ensure all historical data was properly migrated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sample data"
          }), ": Manually review a sample of events from different periods to ensure data format and content consistency."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "By following these steps, you'll create a unified dataset in your Hightouch tables that combines your historical Segment data with new events from Hightouch Events. This approach ensures data continuity and provides a single source of truth for all your event data."
      }), "\n", _jsx(_components.p, {
        children: "Remember to adjust the SQL queries as needed to match your specific schema and table names. Also, consider running these operations in batches if you're dealing with a large volume of historical data."
      }), "\n", _jsxs(_components.p, {
        children: ["In the next section, you'll ", _jsx(_components.a, {
          href: "updating-downstream-applications",
          children: "update downstream applications and destinations"
        }), " so each consumer reads from Hightouch Events."]
      })]
    });
  }
}
export default MDXContent;
